import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { UpgradeModalContext, UserContext, VibratoClientContext } from "@/components/dashboard/contexts"
import { CreditCard, NavArrowRight } from "iconoir-react"
import { settings } from "@/settings"

const planToDisplay: Record<string, string> = {
  plus: "Vibrato Plus",
  pro: "Vibrato for Business - Pro",
  scale: "Vibrato for Business - Scale",
  enterprise: "Vibrato for Business - Enterprise",
  lite: "Vibrato Lite",
  standard: "Vibrato Standard"
}

const planToCredits: Record<string, string> = {
  plus: "Unlimited",
  pro: "5000",
  scale: "20000",
  lite: "500",
  enterprise: "Variable",
  standard: "1000"
}

function IOSBillingSettings() {
  const user = useContext(UserContext)
  const navigate = useNavigate()
  const vibratoClient = useContext(VibratoClientContext)
  const [creditsUsed, setCreditsUsed] = useState(0)
  const [billingCycleStart, setBillingCycleStart] = useState(null)
  const [billingCycleEnd, setBillingCycleEnd] = useState(null)

  useEffect(() => {
    if (user.subscription_provider != "apple") {
      return
    }

    vibratoClient.getSubscription().then((data: any) => {
      setCreditsUsed(data?.credits_used || 0)
      setBillingCycleStart(data?.period_start_at)
      setBillingCycleEnd(data?.period_end_at)
    })
  }, [vibratoClient, user])

  if (!user?.plan || user.plan == "freemium") {
    navigate("/dashboard")
    return null
  }

  return (
    <div className="h-full w-full flex flex-col gap-4 sm:gap-6 pb-12">
      <div className="h-fit bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden flex-shrink-0">
        <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">Current Plan Details</h2>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Current Plan</label>
              <div className="text-base sm:text-lg font-semibold text-purple-600">
                {planToDisplay?.[user.plan] || "Free"}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Plan Credits Per Month</label>
              <div className="text-base sm:text-lg font-medium text-gray-900">{planToCredits[user.plan]}</div>
            </div>

            {billingCycleStart && billingCycleEnd && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Billing Cycle</label>
                <div className="text-sm sm:text-base text-gray-900">
                  {new Date(billingCycleStart).toLocaleDateString()} - {new Date(billingCycleEnd).toLocaleDateString()}
                </div>
              </div>
            )}

            {["pro", "scale", "enterprise", "lite", "standard"].includes(user.plan) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Credits Used This Month</label>
                <div className="text-sm sm:text-base text-gray-900">
                  {user.plan == "plus" ? "N/A" : creditsUsed.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* iOS-specific subscription management section */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200">
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Manage Your Subscription</h3>
          </div>

          {user.subscription_provider === "stripe" ? (
            <>
              <p className="text-sm sm:text-base text-gray-700 mb-4 leading-relaxed">
                To make changes to your subscription, update billing information, or explore different plans, please
                visit our website where you have full control over your account.
              </p>

              <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-purple-100">
                <p className="text-xs sm:text-sm text-gray-700 mb-3">
                  Visit{" "}
                  <a
                    href="https://app.getvibrato.com/dashboard?show_pricing=true"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-purple-600 hover:text-purple-700 underline decoration-purple-300 hover:decoration-purple-500"
                  >
                    app.getvibrato.com
                  </a>{" "}
                  to:
                </p>
                <ul className="text-xs sm:text-sm text-gray-600 space-y-1.5 sm:space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Upgrade or downgrade your subscription plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Update payment methods and billing information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>View detailed billing history and invoices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Cancel or pause your subscription if needed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Access exclusive web-only features and settings</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                  <span>Any changes made on the website will automatically sync with your app</span>
                </div>

                <button
                  onClick={() => window.open("https://app.getvibrato.com/dashboard?show_pricing=true", "_blank")}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center gap-2"
                >
                  Manage Account
                  <NavArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm sm:text-base text-gray-700 mb-4 leading-relaxed">
                Your subscription is managed through Apple. To make changes to your subscription, you'll need to use
                your Apple device settings.
              </p>

              <div className="bg-white rounded-lg p-3 sm:p-4 mb-4 border border-purple-100">
                <p className="text-xs sm:text-sm text-gray-700 mb-3">To manage your Apple subscription:</p>
                <ul className="text-xs sm:text-sm text-gray-600 space-y-1.5 sm:space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>
                      Open <strong>Settings</strong> on your iPhone or iPad
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>Tap your name at the top of the screen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>
                      Tap <strong>Subscriptions</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                    <span>
                      Find and tap <strong>Vibrato</strong> to manage your subscription
                    </span>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function PlanSettings() {
  const user = useContext(UserContext)
  const navigate = useNavigate()
  const vibratoClient = useContext(VibratoClientContext)
  const upgradeModal = useContext(UpgradeModalContext)
  const [creditsUsed, setCreditsUsed] = useState(0)
  const [billingCycleStart, setBillingCycleStart] = useState(null)
  const [billingCycleEnd, setBillingCycleEnd] = useState(null)

  // Show iOS-specific version for iOS users
  if (settings.IS_IOS) {
    return <IOSBillingSettings />
  }

  const redirectToStripePortal = () => {
    vibratoClient
      .getUserStripeDetails()
      .then((details: any) => {
        window.location.href = details.stripe_portal_url
      })
      .catch((err: any) => {
        console.error("Error fetching stripe portal url", err)
      })
  }

  const showUpgradeModal = () => {
    upgradeModal.setInitialView("individual")
    upgradeModal.setShowModal(true)
  }

  useEffect(() => {
    if (user.subscription_provider != "stripe") {
      return
    }

    vibratoClient.getSubscription().then((data: any) => {
      setCreditsUsed(data?.credits_used || 0)
      setBillingCycleStart(data?.period_start_at)
      setBillingCycleEnd(data?.period_end_at)
    })
  }, [vibratoClient])

  if (!user?.plan || user.plan == "freemium") {
    navigate("/dashboard")
    return null
  }

  return (
    <div className="h-full w-full flex flex-col gap-6">
      <div className="bg-white rounded-xl shadow-md border border-gray-200">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-xl">
          <h2 className="text-lg font-semibold text-gray-900">Current Plan Details</h2>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Current Plan</label>
              <div className="text-lg font-semibold text-purple-600">{planToDisplay?.[user.plan] || "Free"}</div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Plan Credits Per Month</label>
              <div className="text-lg font-medium text-gray-900">{planToCredits[user.plan]}</div>
            </div>

            {billingCycleStart && billingCycleEnd && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Billing Cycle</label>
                <div className="text-base text-gray-900">
                  {new Date(billingCycleStart).toLocaleDateString()} - {new Date(billingCycleEnd).toLocaleDateString()}
                </div>
              </div>
            )}

            {["pro", "scale", "enterprise", "lite", "standard"].includes(user.plan) && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Credits Used This Month</label>
                <div className="text-base text-gray-900">
                  {user.plan == "plus" ? "N/A" : creditsUsed.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apple subscription management section for non-iOS */}
      {user.subscription_provider === "apple" && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Manage Your Apple Subscription</h3>
            </div>

            <p className="text-base text-gray-700 mb-4 leading-relaxed">
              Your subscription is managed through Apple. To make changes to your subscription, you'll need to use your
              Apple device settings.
            </p>

            <div className="bg-white rounded-lg p-4 mb-4 border border-purple-100">
              <p className="text-sm text-gray-700 mb-3">To manage your Apple subscription:</p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                  <span>
                    Open <strong>Settings</strong> on your iPhone or iPad
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                  <span>Tap your name at the top of the screen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                  <span>
                    Tap <strong>Subscriptions</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-0.5 flex-shrink-0">•</span>
                  <span>
                    Find and tap <strong>Vibrato</strong> to manage your subscription
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Stripe subscription management buttons */}
      {user.subscription_provider === "stripe" && (
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={redirectToStripePortal}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            Manage Subscription
          </button>

          <button
            onClick={showUpgradeModal}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            Change Subscription
          </button>
        </div>
      )}
    </div>
  )
}
